import React from 'react';
import {
    Box,
    Typography,
    FormControlLabel,
    Checkbox,
    Paper,
    TextField,
    InputAdornment,
    Button,
    Chip,
    MenuItem,
    ListItemText,
    Collapse,
    CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import _ from 'lodash';
import Scrollbar from '../scrollbar';


const SettingsTab = ({
    aiSettings,
    setAiSettings,
    facilities,
    searchText,
    setSearchText,
    handleSelectAllFacilities,
    handleFacilityToggle,
    loading = false,
    loadingSettings = false
}) => {
    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '16px',
            height: '100%',
            overflow: 'auto'
        }}>
            {loadingSettings ? (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '200px',
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ color: '#6c757d' }}>
                        Loading AI settings...
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', margin: 0 }}>
                            AI Settings
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6c757d', margin: 0, flex: 1, minWidth: '300px' }}>
                            Configure AI auto-fill settings and select which facilities can use AI for this question.
                        </Typography>
                    </Box>

            {/* Enable AI */}
            <Paper sx={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={aiSettings.aiEnabled}
                            onChange={(e) => setAiSettings(prev => ({
                                ...prev,
                                aiEnabled: e.target.checked
                            }))}
                            sx={{ '&.Mui-checked': { color: '#1976d2' } }}
                        />
                    }
                    label={
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                            Enable AI auto-fill for this question
                        </Typography>
                    }
                />
            </Paper>

            {/* Facility Selection */}
            {aiSettings.aiEnabled && (
                <Collapse in={aiSettings.aiEnabled}>
                    <Paper sx={{
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid #e9ecef'
                    }}>
                        <Typography variant="h6" sx={{
                            marginBottom: '20px',
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#2c3e50',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <Box sx={{
                                width: '4px',
                                height: '24px',
                                backgroundColor: '#1976d2',
                                borderRadius: '2px'
                            }} />
                            Select Facilities for AI
                        </Typography>

                        {/* Search Field */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search facilities..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#6c757d' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                marginBottom: '16px',
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#1976d2'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#1976d2',
                                        borderWidth: '2px'
                                    }
                                }
                            }}
                        />

                        {/* Select All Button and Count */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '16px'
                        }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleSelectAllFacilities}
                                sx={{
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: '#1976d2',
                                    color: '#1976d2',
                                    '&:hover': {
                                        backgroundColor: '#1976d2',
                                        color: 'white'
                                    }
                                }}
                            >
                                {aiSettings.facilityIds.length === facilities?.length ? 'Deselect All' : 'Select All'}
                            </Button>
                            <Chip
                                label={`${aiSettings.facilityIds.length} of ${facilities?.length || 0} selected`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        </Box>

                        {/* Facilities List */}
                        <Paper sx={{
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            maxHeight: '320px',
                            overflow: 'hidden',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}>
                            <Scrollbar sx={{ height: 320 }}>
                                {loading ? (
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '40px 16px',
                                        flexDirection: 'column',
                                        gap: '16px'
                                    }}>
                                        <CircularProgress size={24} />
                                        <Typography sx={{ fontSize: '14px', color: '#6c757d' }}>
                                            Loading facilities...
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        {facilities && Array.isArray(facilities) &&
                                            _.orderBy(facilities, "name", "ASC")
                                                ?.filter(ele => ele.name?.toLowerCase().includes(searchText?.toLowerCase()))
                                                .map((facility) => (
                                                    <MenuItem
                                                        key={facility._id}
                                                        selected={aiSettings.facilityIds.includes(facility._id)}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleFacilityToggle(facility._id);
                                                        }}
                                                        sx={{
                                                            padding: '12px 16px',
                                                            margin: '4px 8px',
                                                            borderRadius: '8px',
                                                            transition: 'all 0.2s ease',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                backgroundColor: aiSettings.facilityIds.includes(facility._id)
                                                                    ? '#1565c0'
                                                                    : '#f1f3f4'
                                                            },
                                                            "&.Mui-selected": {
                                                                backgroundColor: "#1976d2",
                                                                color: 'white',
                                                                '&:hover': {
                                                                    backgroundColor: '#1565c0'
                                                                }
                                                            },
                                                        }}
                                                    >
                                                        <ListItemText sx={{ margin: 0 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                <Checkbox
                                                                    checked={aiSettings.facilityIds.includes(facility._id)}
                                                                    onChange={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleFacilityToggle(facility._id);
                                                                    }}
                                                                    size="small"
                                                                    sx={{
                                                                        padding: 0,
                                                                        color: aiSettings.facilityIds.includes(facility._id) ? "white" : "#6c757d",
                                                                        "&.Mui-checked": {
                                                                            color: "white"
                                                                        }
                                                                    }}
                                                                />
                                                                <Typography sx={{
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                    color: aiSettings.facilityIds.includes(facility._id) ? 'white' : '#2c3e50'
                                                                }}>
                                                                    {facility.name}
                                                                </Typography>
                                                            </Box>
                                                        </ListItemText>
                                                    </MenuItem>
                                                ))
                                        }
                                        {Array.isArray(facilities) && (facilities?.length === 0 || facilities?.filter(ele => ele.name?.toLowerCase().includes(searchText?.toLowerCase())).length === 0) &&
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '40px 16px',
                                                color: '#6c757d'
                                            }}>
                                                <Typography sx={{ fontSize: '14px', fontStyle: 'italic' }}>
                                                    No facilities found
                                                </Typography>
                                            </Box>
                                        }
                                    </>
                                )}
                            </Scrollbar>
                        </Paper>
                    </Paper>
                </Collapse>
            )}
                </>
            )}
        </Box>
    );
};

export default SettingsTab;